import React, { useState, useEffect, useCallback, useRef } from 'react';
import * as Dialog from '@radix-ui/react-dialog'; // Import Radix Dialog
import { Cross2Icon } from '@radix-ui/react-icons'; // Optional: For a close icon
import './BetAmountInput.css';

interface BetAmountInputProps {
  show: boolean; // Controls the Dialog open state
  onConfirm: (amount: number) => void;
  onCancel: () => void; // Called when Dialog wants to close
  minBet: number; // Minimum legal bet/raise
  maxBet: number; // Player's effective stack (max possible bet/raise)
  actionType: 'bet' | 'raise'; // To customize the title/button
  currentBetToCall?: number; // Amount needed to call (for raise sizing context)
}

const BetAmountInput: React.FC<BetAmountInputProps> = ({
  show,
  onConfirm,
  onCancel,
  minBet,
  maxBet,
  actionType,
  currentBetToCall = 0, // Default to 0 if not provided
}) => {
  const [amount, setAmount] = useState<string>('');
  const [selectedQuickBet, setSelectedQuickBet] = useState<number | null>(null);
  const inputRef = useRef<HTMLInputElement>(null); // Ref for the input element

  // Focus and select input when shown
  useEffect(() => {
    if (show) {
      const initialValue = String(minBet);
      setAmount(initialValue);
      setSelectedQuickBet(null);
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus();
          inputRef.current.select();
        }
      }, 50);
    } else {
      setAmount('');
      setSelectedQuickBet(null);
    }
  }, [show, minBet]); // Dependency on minBet ensures it resets if minBet changes while open

  const handleConfirm = useCallback(() => {
    const numericAmount = parseInt(amount, 10);
    if (!isNaN(numericAmount) && numericAmount >= minBet && numericAmount <= maxBet) {
      onConfirm(numericAmount); // Let PlaygroundPage handle closing via 'show'
    } else {
      // Handle invalid input? Maybe flash border red? For now, just don't confirm.
      console.warn(`Invalid amount entered: ${amount}. Min: ${minBet}, Max: ${maxBet}`);
    }
  }, [amount, minBet, maxBet, onConfirm]);

  // Handle Enter key press
  const handleKeyDown = useCallback((event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      handleConfirm();
    } else if (event.key === 'Escape') {
      onCancel(); // Dialog will close via onOpenChange
    }
  }, [handleConfirm, onCancel]);

  // --- Input Sanitization --- 
  const handleAmountChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = event.target.value;
    // Allow only digits (remove any non-digit characters)
    const sanitizedValue = rawValue.replace(/\D/g, '');
    setAmount(sanitizedValue); 
    setSelectedQuickBet(null); // Clear quick bet selection when manually typing
  };
  // --- End Sanitization ---

  // We control open state via the 'show' prop passed down
  // Radix Dialog's onOpenChange will call our onCancel when it wants to close
  // (e.g., Escape key, overlay click)

  const title = actionType === 'bet' ? 'Enter Bet Amount' : 'Enter Raise Amount';
  const confirmButtonText = actionType === 'bet' ? 'Bet' : 'Raise';

  // Quick bet sizing options
  const potSize = Math.max(currentBetToCall * 3, minBet * 10); // Better pot estimation
  const quickBetOptions = [
    { label: '1/3 Pot', amount: Math.max(minBet, Math.min(maxBet, Math.round(potSize * 0.33))) },
    { label: '1/2 Pot', amount: Math.max(minBet, Math.min(maxBet, Math.round(potSize * 0.5))) },
    { label: '2/3 Pot', amount: Math.max(minBet, Math.min(maxBet, Math.round(potSize * 0.67))) },
    { label: 'Pot', amount: Math.max(minBet, Math.min(maxBet, Math.round(potSize))) },
    { label: 'All-In', amount: maxBet },
  ];

  const handleQuickBet = (option: typeof quickBetOptions[0], index: number) => {
    setAmount(String(option.amount));
    setSelectedQuickBet(index);
  };

  return (
    <Dialog.Root open={show} onOpenChange={(open: boolean) => !open && onCancel()}> 
      {/* <Dialog.Trigger /> We don't need a trigger, it's controlled externally */}
      <Dialog.Portal> 
        <Dialog.Overlay className="bet-dialog-overlay" /> 
        <Dialog.Content className="bet-dialog-content"> 
          {/* Header */}
          <div className="bet-dialog-header">
            <Dialog.Title className="bet-dialog-title">{title}</Dialog.Title>
            <Dialog.Close asChild>
              <button className="bet-dialog-close" aria-label="Close">
                <Cross2Icon />
              </button>
            </Dialog.Close>
          </div>

          {/* Body */}
          <div className="bet-dialog-body">
            {/* Amount Input Section */}
            <div className="bet-amount-section">
              <label className="bet-amount-label">Amount to {actionType}:</label>
              <div className="bet-amount-input-wrapper">
                <span className="bet-currency-symbol">$</span>
                <input
                  ref={inputRef} // Assign the ref
                  type="text"   // Change type to text to prevent browser number quirks
                  inputMode="numeric" // Hint for mobile numeric keyboard
                  pattern="[0-9]*"    // Pattern for validation (optional but good practice)
                  value={amount}
                  onChange={handleAmountChange} // Use the sanitized handler
                  onKeyDown={handleKeyDown}
                  // min/max don't strictly apply to type="text", validation is in handleConfirm
                  step="1"
                  className="bet-amount-input"
                  // autoFocus prop can sometimes interfere with manual focus, let's remove it
                  // autoFocus 
                  onFocus={(e) => e.target.select()} // Keep onFocus for good measure
                  placeholder={String(minBet)}
                />
              </div>
              <div className="bet-amount-limits">
                <span>Min: ${minBet.toLocaleString()}</span>
                <span>Max: ${maxBet.toLocaleString()}</span>
              </div>
            </div>

            {/* Quick Bet Buttons Section */}
            <div className="bet-quick-section">
              <label className="bet-quick-label">Quick Bets:</label>
              <div className="bet-quick-buttons">
                {quickBetOptions.map((option, index) => (
                  <button
                    key={index}
                    className={`bet-quick-btn ${selectedQuickBet === index ? 'selected' : ''}`}
                    onClick={() => handleQuickBet(option, index)}
                    disabled={option.amount < minBet || option.amount > maxBet}
                  >
                    <span className="bet-quick-label-text">{option.label}</span>
                    <span className="bet-quick-amount">${option.amount.toLocaleString()}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Footer Actions */}
          <div className="bet-dialog-actions">
            <Dialog.Close asChild>
              <button onClick={onCancel} className="bet-cancel-btn">
                Cancel
              </button>
            </Dialog.Close>
            <button onClick={handleConfirm} className="bet-confirm-btn">
              {confirmButtonText} {amount ? `$${parseInt(amount).toLocaleString()}` : ''}
            </button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

export default BetAmountInput; 